/**
 * DemandDetailsDrawer — Painel lateral com metadados da demanda.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { TaskComplementaryDataPanel } from './TaskComplementaryDataPanel';

interface DemandDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  executionCode: string;
  instanceTitle?: string | null;
  requesterName: string;
  openedAtLabel: string;
  slaLabel: string;
  slaStatus: 'ok' | 'warning' | 'violated' | 'none';
  riskLabel: string;
  riskOk?: boolean;
  priority?: string | null;
  originLabel?: string | null;
  children?: React.ReactNode;
}

export const DemandDetailsDrawer: React.FC<DemandDetailsDrawerProps> = ({
  open,
  onClose,
  executionCode,
  instanceTitle,
  requesterName,
  openedAtLabel,
  slaLabel,
  slaStatus,
  riskLabel,
  riskOk,
  priority,
  originLabel,
  children,
}) => {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/25 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Detalhes da Demanda"
        className="fixed inset-y-0 right-0 z-50 flex w-[min(400px,92vw)] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-[var(--border-default)] dark:bg-[var(--bg-elevated)]"
      >
        <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-[var(--border-default)]">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-blue-600">Detalhes da Demanda</p>
            <p className="mt-0.5 font-mono text-[11px] font-semibold text-slate-700 dark:text-slate-200">{executionCode}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-[var(--bg-surface)]"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <TaskComplementaryDataPanel
            executionCode={executionCode}
            instanceTitle={instanceTitle}
            requesterName={requesterName}
            openedAtLabel={openedAtLabel}
            slaLabel={slaLabel}
            slaStatus={slaStatus}
            riskLabel={riskLabel}
            riskOk={riskOk}
            priority={priority}
          />
          {originLabel && (
            <div className="border-t border-slate-100 px-4 py-2.5 dark:border-[var(--border-default)]">
              <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-slate-400">Origem</p>
              <p className="mt-0.5 text-[11px] font-semibold text-slate-800 dark:text-slate-200">{originLabel}</p>
            </div>
          )}
          {children}
        </div>
      </aside>
    </>,
    document.body,
  );
};

export default DemandDetailsDrawer;
